import {FetchBaseQueryError} from '@reduxjs/toolkit/query';

import {isNil, isNotNil} from 'ramda';

import {omneticApi} from '@dms/api/base';

import {assignSafely, getByPathSafely, getSafely} from 'shared/web';

import {
  updateAuctionDetailCacheByExternalId,
  updateAuctionVehicleSummaryInCaches,
} from '../sourcingAuction/api';
import {ROWS_PER_PAGE} from './constants/rowsPerPage';
import {
  AdServerRedirectApiArg,
  AdServerRedirectResponse,
  AllowedCountryAndCurrencyApiArg,
  AllowedCountryAndCurrencyApiResponse,
  BulkCreateUserCarvagoBuyApiArg,
  BulkCreateUserCarvagoBuyApiResponse,
  BulkCreateUserComparisonApiArg,
  BulkCreateUserComparisonApiResponse,
  BulkCreateUserFavouriteApiArg,
  BulkCreateUserFavouriteApiResponse,
  BulkCreateUserHiddenApiArg,
  BulkCreateUserHiddenApiResponse,
  BulkCreateUserToBuyApiArg,
  BulkCreateUserToBuyApiResponse,
  BulkDeleteUserCarvagoBuyApiArg,
  BulkDeleteUserCarvagoBuyApiResponse,
  BulkDeleteUserComparisonApiArg,
  BulkDeleteUserComparisonApiResponse,
  BulkDeleteUserFavouriteApiArg,
  BulkDeleteUserFavouriteApiResponse,
  BulkDeleteUserHiddenApiArg,
  BulkDeleteUserHiddenApiResponse,
  BulkDeleteUserToBuyApiArg,
  BulkDeleteUserToBuyApiResponse,
  CreatePresetApiResponse,
  CreateUserCarvagoBuyApiArg,
  CreateUserCarvagoBuyApiResponse,
  CreateUserSeenApiArg,
  CreateUserSeenApiResponse,
  CreateUserVehicleCostApiArg,
  CreateUserVehicleCostsApiArg,
  CreateUserVehicleSummaryApiArg,
  CreateUserVehicleSummaryRequestBody,
  DeleteUserCarvagoBuyApiArg,
  DeleteUserCarvagoBuyApiResponse,
  DeleteUserVehicleCostApiArg,
  DeletePresetApiResponse,
  DetailDefaultUserPresetCostApiResponse,
  DetailUserCostVehicleNoteApiArg,
  DetailUserCostVehicleNoteApiResponse,
  DrillDownApiArg,
  DrillDownResponse,
  ExportAllUserToBuyToCsvApiArg,
  ExportVehiclesToCsvApiArg,
  GetCarFeaturesApiArg,
  GetCarFeaturesApiResponse,
  GetPresetApiArg,
  GetPresetApiResponse,
  GetPresetsListApiArg,
  GetPresetsListApiResponse,
  GetPriceMapApiResponse,
  GetPriceMapApiArg,
  GetSourcingVehicleCountApiArg,
  GetSourcingVehicleCountApiResponse,
  HighlightResponseBody,
  ListUserCarvagoBuyApiArg,
  ListUserCarvagoBuyApiResponse,
  ShortcomingResponseBody,
  ListUserComparisonApiArg,
  ListUserComparisonApiResponse,
  ListUserFavouriteApiArg,
  ListUserFavouriteApiResponse,
  ListUserHiddenApiArg,
  ListUserHiddenApiResponse,
  ListUserToBuyApiArg,
  ListUserToBuyApiResponse,
  PriceMapStatistics,
  PushVehiclesToSaleApiArg,
  RemoveHighlightBulkApiArg,
  RemoveHighlightBulkApiResponse,
  RemoveShortcomingBulkApiArg,
  RemoveShortcomingBulkApiResponse,
  ReorderPresetsApiArg,
  ReorderPresetsApiResponse,
  SalesClaim,
  SetUserVehicleCostNoteApiArg,
  SimilarVehicleList,
  SimilarVehicleRequestBody,
  SimilarVehicleStatistics,
  SortUserComparisonRequestApiArg,
  SortUserComparisonRequestBody,
  SourcingCreateCommentApiArg,
  SourcingCreateCommentApiResponse,
  SourcingCreatePresetApiArg,
  SourcingDeleteCommentApiArg,
  SourcingDeletePresetApiArg,
  SourcingGetCommentsApiArg,
  SourcingGetCommentsApiResponse,
  SourcingGetPriceReportApiArg,
  SourcingGetPriceReportApiResponse,
  SourcingGetTopSellingApiArg,
  SourcingGetTopSellingApiResponse,
  SourcingServer,
  SourcingUpdateCommentApiArg,
  SourcingUpdateCommentApiResponse,
  SourcingUpdatePresetApiArg,
  SourcingVehicleDetailApiArg,
  SourcingVehicleDetailApiResponse,
  SourcingVehicleListApiArg,
  SourcingVehicleListApiResponse,
  SourcingVehicleListItemResponseBody,
  UpdatePresetApiResponse,
  UpdateUserVehicleCostApiArg,
  UserCostVehicleListApiArg,
  UserCostVehicleListApiResponse,
  VehicleTypeForPriceMap,
  CreateSourcingVehicleOfferApiResponse,
  CreateSourcingVehicleOfferApiArg,
  GetSourcingVehicleOfferApiResponse,
  GetSourcingVehicleOfferApiArg,
} from './types';
import {filterOutDuplicates} from './utils/filterOutDuplicates';
import {fixAlfaBugsInDrillDownResponse} from './utils/fixAlfaBugsInDrillDownResponse';
import {reduceArrayToObject} from './utils/reduceArrayToObject';
import {renameStatisticsKeyToString} from './utils/renameStatisticsKeyToString';
import {
  isSourcingListEndpointName,
  List,
  OmneticMutationLifecycleApi,
  setUserVehicleSummary,
} from './utils/sourcingApiHelpers';

const getOffset = (page: number = 1) => (page - 1) * ROWS_PER_PAGE;

const sourcingRtkApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    exportAllUserToBuyToCsv: build.query<unknown, ExportAllUserToBuyToCsvApiArg>({
      // cannot be mutation, because RTK Query does not allow POST without arguments
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-to-buy/export/csv`,
        responseHandler: 'text',
        cache: 'no-cache',
        params: {
          buyerCountry: queryArg.buyerCountry,
          currency: queryArg.currency,
          margin: queryArg.margin,
          orderBy: queryArg.orderBy,
        },
      }),
    }),
    sourcingCreatePreset: build.mutation<CreatePresetApiResponse, SourcingCreatePresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/filter-presets`,
        method: 'POST',
        body: queryArg.createPresetRequestBody,
      }),
      invalidatesTags: () => [{type: 'SourcingFilterPresetList'}],
    }),
    sourcingGetPresetsList: build.query<GetPresetsListApiResponse, GetPresetsListApiArg>({
      query: (queryArg) => ({
        url: '/dms/v1/sourcing/filter-presets',
        params: {country: queryArg.country, currency: queryArg.currency},
      }),
      providesTags: (result) =>
        result
          ? [
              {type: 'SourcingFilterPresetList'},
              ...result.map((item) => ({type: 'SourcingFilterPreset' as const, id: item.id})),
            ]
          : [{type: 'SourcingFilterPresetList'}],
    }),
    sourcingUpdatePreset: build.mutation<UpdatePresetApiResponse, SourcingUpdatePresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/filter-presets/${queryArg.id}`,
        method: 'PATCH',
        body: queryArg.updatePresetRequestBody,
      }),
      invalidatesTags: (_, __, queryArg) => [{type: 'SourcingFilterPreset', id: queryArg.id}],
    }),
    sourcingDeletePreset: build.mutation<DeletePresetApiResponse, SourcingDeletePresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/filter-presets/${queryArg.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, queryArg) => [{type: 'SourcingFilterPreset', id: queryArg.id}],
    }),
    sourcingGetPreset: build.query<GetPresetApiResponse, GetPresetApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/filter-presets/${queryArg.id}`,
      }),
    }),
    sourcingReorderPresets: build.mutation<ReorderPresetsApiResponse, ReorderPresetsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/filter-presets/reorder`,
        method: 'PUT',
        body: queryArg.reorderPresetsRequestBody,
      }),
      onQueryStarted: ({reorderPresetsRequestBody: body}, {dispatch, getState, queryFulfilled}) => {
        // OPTIMISTIC RESPONSE
        const queriesOriginalArgs = sourcingRtkApi.util
          .selectInvalidatedBy(getState(), [{type: 'SourcingFilterPresetList'}])
          .map((item) => item.originalArgs);
        queriesOriginalArgs.forEach((originalArgs) => {
          const patchResult = dispatch(
            sourcingRtkApi.util.updateQueryData('sourcingGetPresetsList', originalArgs, (draft) => {
              draft.forEach((item) => {
                const position = body.items.find((reqItem) => reqItem.id === item.id)?.position;
                if (isNotNil(position)) {
                  item.position = position;
                }
              });
            })
          );
          queryFulfilled.catch(patchResult.undo);
        });
      },
      invalidatesTags: () => [{type: 'SourcingFilterPresetList'}],
    }),
    sourcingGetCarFeatures: build.query<GetCarFeaturesApiResponse, GetCarFeaturesApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/car-features`,
        method: 'POST',
        body: queryArg.body,
      }),
    }),
    sourcingGetTopSelling: build.query<
      SourcingGetTopSellingApiResponse,
      SourcingGetTopSellingApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/sold-vehicle/top-selling`,
        method: 'POST',
        body: queryArg.body,
        params: {size: queryArg.size},
      }),
    }),
    sourcingGetPriceReport: build.mutation<
      SourcingGetPriceReportApiResponse,
      SourcingGetPriceReportApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/price-report`,
        method: 'POST',
        body: queryArg,
      }),
    }),
    sourcingVehicleDetail: build.query<
      SourcingVehicleDetailApiResponse,
      SourcingVehicleDetailApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/${queryArg.externalId}`,
        params: {
          marginLte: queryArg.marginLte,
          marginGte: queryArg.marginGte,
          positionGte: queryArg.positionGte,
          positionLte: queryArg.positionLte,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
        },
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SourcingVehicleDetail', id: queryArg.externalId},
      ],
    }),
    sourcingVehicleList: build.query<SourcingVehicleListApiResponse, SourcingVehicleListApiArg>({
      queryFn: async (queryArg, queryApi, _extraOptions, fetchWithBaseQuery) => {
        const {page, body} = queryArg;
        const initialOffset = getOffset(page);
        const vehicles: SourcingVehicleListItemResponseBody[] = [];
        let hasMore = true;
        let offset = initialOffset;
        let size = 0;

        try {
          while (hasMore) {
            const result = await fetchWithBaseQuery({
              url: `/dms/v1/sourcing-vehicle`,
              method: 'POST',
              body: {...body, offset, size: ROWS_PER_PAGE},
              params: {
                offset,
                size: ROWS_PER_PAGE,
                currency: queryArg.currency,
                orderBy: queryArg.orderBy,
              },
            });

            if (result.error) {
              return {error: result.error as FetchBaseQueryError};
            }

            const fetchedData = result.data as SourcingVehicleListApiResponse;
            if (!queryArg.isPolling) {
              return {data: fetchedData};
            }

            const state = queryApi.getState() as Record<string, unknown>;
            const serializedKey = `sourcingVehicleList_${queryArg.currency}_${queryArg.orderBy}_infinite_${JSON.stringify(queryArg.body)}`;
            const cachedQueryState = getByPathSafely(state, [
              'rtk_omneticApi',
              'queries',
              serializedKey,
            ]) as {data?: SourcingVehicleListApiResponse} | undefined;
            const cachedVehicles = cachedQueryState?.data?.data ?? [];
            vehicles.push(...fetchedData.data);
            const newVehicles = filterOutDuplicates(cachedVehicles, fetchedData.data);
            hasMore = cachedVehicles.length > 0 && newVehicles.length === ROWS_PER_PAGE;
            offset += ROWS_PER_PAGE;
            size += fetchedData.links.size;
          }

          return {data: {data: vehicles, links: {size, offset: initialOffset}}};
        } catch (error) {
          return {error: error as FetchBaseQueryError};
        }
      },
      serializeQueryArgs: ({endpointName, queryArgs}) => {
        /* This is used to be able to merge all pages into one cache entry
         * Polling and infinite scroll have separate cache keys, so that they use different subscriptions
         *  - that is important, otherwise RTK Query will mix up polling and infinite scroll arguments
         */
        const type = queryArgs.isPolling ? 'polling' : 'infinite';
        return `${endpointName}_${queryArgs.currency}_${queryArgs.orderBy}_${type}_${JSON.stringify(queryArgs.body)}`;
      },
      merge: (currentCache, newData, {arg}) => {
        const isPolling = arg.isPolling;
        const newVehicles = filterOutDuplicates(currentCache.data, newData.data);

        if (isPolling) {
          // Polling: prepend new unique vehicles from page 1, keep existing ones
          currentCache.data.unshift(...newVehicles);
        } else {
          // Infinite scroll: append new page data
          currentCache.data.push(...newVehicles);
        }
      },
      forceRefetch: (params) => {
        /* The purpose of this function is to force refetch the same cache entry
            - with the new page = old page + 1
            - when polling to get new vehicles
           But to use cache when navigating back to the listing page
         */
        const isPolling = params.currentArg?.isPolling;
        const currentPage = params.currentArg?.page ?? 1;
        const previousPage = params.previousArg?.page;

        if (isPolling) {
          return true;
        }

        return currentPage === (previousPage ?? 1) + 1;
      },
      onQueryStarted: async (arg, api) => {
        // Sync caches between polling and infinite scroll queries
        // When polling completes, prepend new unique entries to infinite scroll cache
        // When infinite scroll completes, append new entries to polling cache
        try {
          await api.queryFulfilled;
          const currentCache = api.getCacheEntry();
          const currentVehicles = currentCache?.data?.data;
          const oppositeArgs = {...arg, isPolling: arg.isPolling !== true};

          if (!currentVehicles) {
            return;
          }

          api.dispatch(
            sourcingRtkApi.util.updateQueryData('sourcingVehicleList', oppositeArgs, (draft) => {
              const newVehicles = filterOutDuplicates(draft.data, currentVehicles);

              if (newVehicles.length === 0) {
                return;
              }

              if (arg.isPolling === true) {
                // Polling -> infinite scroll: prepend new unique vehicles
                draft.data.unshift(...newVehicles);
              } else {
                // Infinite scroll -> polling: append new page data
                draft.data.push(...newVehicles);
              }
            })
          );
        } catch (error) {
          // Silently fail cache syncing - it's not critical for functionality
          console.error('Cache sync failed', error);
        }
      },
      providesTags: () => [
        {type: 'SourcingAllListsWithSameResponseStructure'},
        {type: 'SourcingVehicleList'},
      ],
      // We need to keep cache 30mins because when you go to vehicle detail and return back we have to scroll to last visited vehicle
      keepUnusedDataFor: 1800,
    }),
    sourcingGetVehicleCount: build.query<
      GetSourcingVehicleCountApiResponse,
      GetSourcingVehicleCountApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/_count`,
        method: 'POST',
        body: queryArg.body,
        params: {currency: queryArg.currency},
      }),
    }),
    sourcingAllowedCountryAndCurrency: build.query<
      AllowedCountryAndCurrencyApiResponse,
      AllowedCountryAndCurrencyApiArg
    >({
      query: () => ({
        url: `/dms/v1/sourcing/allowed-country-and-currency`,
      }),
    }),
    sourcingListUserFavourite: build.query<ListUserFavouriteApiResponse, ListUserFavouriteApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-favourite`,
        params: {
          size: queryArg.size,
          offset: queryArg.offset,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          margin: queryArg.margin,
          orderBy: queryArg.orderBy,
        },
      }),
      providesTags: () => [
        {type: 'SourcingAllListsWithSameResponseStructure'},
        {type: 'SourcingFavouriteList'},
      ],
    }),
    sourcingListUserToBuy: build.query<ListUserToBuyApiResponse, ListUserToBuyApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-to-buy`,
        params: {
          size: queryArg.size,
          offset: queryArg.offset,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          margin: queryArg.margin,
          orderBy: queryArg.orderBy,
        },
      }),
      providesTags: () => [
        {type: 'SourcingAllListsWithSameResponseStructure'},
        {type: 'SourcingToBuyList'},
      ],
    }),
    sourcingListUserHidden: build.query<ListUserHiddenApiResponse, ListUserHiddenApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-hidden`,
        params: {
          size: queryArg.size,
          offset: queryArg.offset,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          margin: queryArg.margin,
          orderBy: queryArg.orderBy,
        },
      }),
      providesTags: () => [
        {type: 'SourcingAllListsWithSameResponseStructure'},
        {type: 'SourcingHiddenList'},
      ],
    }),
    sourcingListUserComparison: build.query<
      ListUserComparisonApiResponse,
      ListUserComparisonApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/sourcing-vehicle/user-comparison',
        params: {
          size: queryArg.size,
          offset: queryArg.offset,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          margin: queryArg.margin,
          orderBy: queryArg.orderBy,
        },
      }),
      providesTags: () => [
        {type: 'SourcingAllListsWithSameResponseStructure'},
        {type: 'SourcingComparisonList'},
      ],
    }),
    sourcingBulkDeleteUserToBuy: build.mutation<
      BulkDeleteUserToBuyApiResponse,
      BulkDeleteUserToBuyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-to-buy/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'sourcingListUserToBuy');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'inCart', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'inCart', false);
      },
      invalidatesTags: [{type: 'User'}, {type: 'SourcingPriceMapVehicles'}],
    }),
    sourcingBulkCreateUserToBuy: build.mutation<
      BulkCreateUserToBuyApiResponse,
      BulkCreateUserToBuyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-to-buy/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'inCart', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'inCart', true);
      },
      invalidatesTags: [
        {type: 'User'},
        {type: 'SourcingToBuyList'},
        {type: 'SourcingPriceMapVehicles'},
      ],
    }),
    sourcingBulkDeleteUserHidden: build.mutation<
      BulkDeleteUserHiddenApiResponse,
      BulkDeleteUserHiddenApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-hidden/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'sourcingListUserHidden');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'hidden', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'hidden', false);
      },
      invalidatesTags: [
        {type: 'User'},
        {type: 'SourcingVehicleList'},
        {type: 'SourcingPriceMapVehicles'},
      ],
    }),
    sourcingBulkCreateUserHidden: build.mutation<
      BulkCreateUserHiddenApiResponse,
      BulkCreateUserHiddenApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-hidden/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'sourcingVehicleList');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'hidden', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'hidden', true);
      },
      invalidatesTags: [
        {type: 'User'},
        {type: 'SourcingHiddenList'},
        {type: 'SourcingPriceMapVehicles'},
      ],
    }),
    sourcingBulkDeleteUserFavourite: build.mutation<
      BulkDeleteUserFavouriteApiResponse,
      BulkDeleteUserFavouriteApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-favourite/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'sourcingListUserFavourite');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'favourite', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'favourite', false);
      },
      invalidatesTags: [{type: 'User'}, {type: 'SourcingPriceMapVehicles'}],
    }),
    sourcingBulkCreateUserFavourite: build.mutation<
      BulkCreateUserFavouriteApiResponse,
      BulkCreateUserFavouriteApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-favourite/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'favourite', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'favourite', true);
      },
      invalidatesTags: [
        {type: 'User'},
        {type: 'SourcingFavouriteList'},
        {type: 'SourcingPriceMapVehicles'},
      ],
    }),
    sourcingReorderUserComparison: build.mutation<void, SortUserComparisonRequestApiArg>({
      query: (queryArg) => {
        const requestBody: SortUserComparisonRequestBody = queryArg.map((vehicle, index) => ({
          externalId: vehicle.sourcingVehicle.adId,
          position: index + 1,
        }));

        return {
          url: '/dms/v1/sourcing-vehicle/user-comparison/sort',
          method: 'POST',
          body: {
            sourcingVehiclesWithPositions: requestBody,
          },
        };
      },
      onQueryStarted(patch, mutationLifecycleApi) {
        sourcingRtkApi.util
          .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingComparisonList'}])
          .forEach(({endpointName, originalArgs}) => {
            if (endpointName !== 'sourcingListUserComparison') {
              throw new Error(`Invalid endpoint name (${endpointName}).`);
            }

            const patchResult = mutationLifecycleApi.dispatch(
              sourcingRtkApi.util.updateQueryData(
                'sourcingListUserComparison',
                originalArgs,
                (draft) => {
                  Object.assign(draft.data, patch);
                }
              )
            );
            mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
          });
      },
      invalidatesTags: [{type: 'SourcingComparisonList'}],
    }),
    sourcingBulkDeleteUserComparison: build.mutation<
      BulkDeleteUserComparisonApiResponse,
      BulkDeleteUserComparisonApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-comparison/_bulk`,
        method: 'DELETE',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(args, mutationLifecycleApi, 'sourcingListUserComparison');
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'comparison', false);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'comparison', false);
      },
      invalidatesTags: [{type: 'User'}, {type: 'SourcingPriceMapVehicles'}],
    }),
    sourcingBulkCreateUserComparison: build.mutation<
      BulkCreateUserComparisonApiResponse,
      BulkCreateUserComparisonApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-comparison/_bulk`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(args, mutationLifecycleApi, 'comparison', true);
        optimisticUpdateVehicleDetailCache(args, mutationLifecycleApi, 'comparison', true);
      },
      invalidatesTags: [{type: 'User'}, {type: 'SourcingPriceMapVehicles'}],
    }),
    sourcingCreateUserVehicleSummary: build.mutation<void, CreateUserVehicleSummaryApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/vehicle-summary/${queryArg.sourcingVehicleId}`,
        method: 'POST',
        body: queryArg.body,
      }),
      onQueryStarted(queryArgs, mutationLifecycleApi) {
        updateClassifiedsVehicleSummaryInCaches(
          queryArgs.sourcingVehicleId,
          queryArgs.body,
          mutationLifecycleApi
        );
        updateAuctionVehicleSummaryInCaches(
          queryArgs.sourcingVehicleId,
          queryArgs.body,
          mutationLifecycleApi
        );
      },
    }),
    sourcingGetSalesClaim: build.query<SalesClaim, {adId: string}>({
      query: ({adId}) => ({
        url: `/dms/v1/sourcing-vehicle/${adId}/sales-claim`,
      }),
    }),
    sourcingSetSalesClaim: build.mutation<SalesClaim, {adId: string; note: string | null}>({
      query: ({adId, note}) => ({
        url: `/dms/v1/sourcing-vehicle/${adId}/sales-claim`,
        method: 'PUT',
        body: {note},
      }),
      async onQueryStarted({adId}, {dispatch, queryFulfilled}) {
        try {
          const {data} = await queryFulfilled;
          dispatch(sourcingRtkApi.util.upsertQueryData('sourcingGetSalesClaim', {adId}, data));
        } catch {}
      },
    }),
    sourcingSetFeatureAsHighlighted: build.mutation<void, {externalId: string; featureKey: string}>(
      {
        query: ({externalId, featureKey}) => ({
          url: `/dms/v1/sourcing-vehicle/${externalId}/feature/${featureKey}`,
          method: 'PUT',
        }),
        onQueryStarted: (queryArgs, mutationLifecycleApi) => {
          updateFeatureHighlightInCaches(
            queryArgs.externalId,
            queryArgs.featureKey,
            true,
            mutationLifecycleApi
          );
        },
      }
    ),
    sourcingSetFeatureAsNotHighlighted: build.mutation<
      void,
      {externalId: string; featureKey: string}
    >({
      query: ({externalId, featureKey}) => ({
        url: `/dms/v1/sourcing-vehicle/${externalId}/feature/${featureKey}`,
        method: 'DELETE',
      }),
      onQueryStarted: (queryArgs, mutationLifecycleApi) => {
        updateFeatureHighlightInCaches(
          queryArgs.externalId,
          queryArgs.featureKey,
          false,
          mutationLifecycleApi
        );
      },
    }),
    sourcingAddVehicleHighlightBulk: build.mutation<
      {highlights: HighlightResponseBody[]},
      {externalId: string; highlights: string[]}
    >({
      query: ({externalId, highlights}) => ({
        url: `/dms/v1/sourcing-vehicle/${externalId}/highlight/_bulk`,
        method: 'PUT',
        body: {highlights: highlights.map((highlight) => ({text: highlight}))},
      }),
      onQueryStarted: async (queryArgs, mutationLifecycleApi) => {
        const {data} = await mutationLifecycleApi.queryFulfilled;
        updateHighlightsInCaches(queryArgs.externalId, data.highlights, mutationLifecycleApi);
      },
    }),
    sourcingRemoveVehicleHighlight: build.mutation<void, {externalId: string; highlightId: string}>(
      {
        query: ({externalId, highlightId}) => ({
          url: `/dms/v1/sourcing-vehicle/${externalId}/highlight/${highlightId}`,
          method: 'DELETE',
        }),
        onQueryStarted: (queryArgs, mutationLifecycleApi) => {
          removeHighlightFromCaches(
            queryArgs.externalId,
            queryArgs.highlightId,
            mutationLifecycleApi
          );
        },
      }
    ),
    sourcingRemoveVehicleHighlightBulk: build.mutation<
      RemoveHighlightBulkApiResponse,
      RemoveHighlightBulkApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/${queryArg.externalId}/highlight/bulk-delete`,
        method: 'PUT',
        body: queryArg.removeHighlightBulkRequestBody,
      }),
      onQueryStarted: (queryArgs, mutationLifecycleApi) => {
        removeHighlightsBulkFromCaches(
          queryArgs.externalId,
          queryArgs.removeHighlightBulkRequestBody.highlightIds,
          mutationLifecycleApi
        );
      },
    }),
    sourcingAddVehicleShortcomingBulk: build.mutation<
      {shortcomings: HighlightResponseBody[]},
      {externalId: string; shortcomings: string[]}
    >({
      query: ({externalId, shortcomings}) => ({
        url: `/dms/v1/sourcing-vehicle/${externalId}/shortcoming/_bulk`,
        method: 'PUT',
        body: {shortcomings: shortcomings.map((shortcoming) => ({text: shortcoming}))},
      }),
      onQueryStarted: async (queryArgs, mutationLifecycleApi) => {
        const {data} = await mutationLifecycleApi.queryFulfilled;
        updateShortcomingsInCaches(queryArgs.externalId, data.shortcomings, mutationLifecycleApi);
      },
    }),
    sourcingRemoveVehicleShortcoming: build.mutation<
      void,
      {externalId: string; shortcomingId: string}
    >({
      query: ({externalId, shortcomingId}) => ({
        url: `/dms/v1/sourcing-vehicle/${externalId}/shortcoming/${shortcomingId}`,
        method: 'DELETE',
      }),
      onQueryStarted: (queryArgs, mutationLifecycleApi) => {
        removeShortcomingFromCaches(
          queryArgs.externalId,
          queryArgs.shortcomingId,
          mutationLifecycleApi
        );
      },
    }),
    sourcingRemoveVehicleShortcomingBulk: build.mutation<
      RemoveShortcomingBulkApiResponse,
      RemoveShortcomingBulkApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/${queryArg.externalId}/shortcoming/bulk-delete`,
        method: 'PUT',
        body: queryArg.removeShortcomingBulkRequestBody,
      }),
      onQueryStarted: (queryArgs, mutationLifecycleApi) => {
        removeShortcomingsBulkFromCaches(
          queryArgs.externalId,
          queryArgs.removeShortcomingBulkRequestBody.shortcomingIds,
          mutationLifecycleApi
        );
      },
    }),
    /** @deprecated Use sourcingGetPriceMapStatistics or alternative endpoint instead. */
    sourcingGetPriceMapVehicles: build.query<
      Record<string, VehicleTypeForPriceMap>,
      SimilarVehicleRequestBody
    >({
      query: (queryArg) => ({
        url: '/dms/v1/sourcing/similar-vehicle/price-map',
        method: 'GET',
        params: queryArg,
      }),
      transformResponse: (response: SimilarVehicleList) =>
        reduceArrayToObject<VehicleTypeForPriceMap>(
          //@ts-expect-error Unable to fix reduceArrayToObject types
          Object.entries(response).reduce(
            //@ts-expect-error Unable to fix reduce types
            (prev, [type, value]) => [
              ...prev,
              ...value.map((vehicle) => ({
                ...vehicle,
                type,
                price: vehicle.sourcingVehicle?.price?.withVat,
                mileage: vehicle.sourcingVehicle?.mileage ?? 0,
              })),
            ],
            []
          ),
          ['sourcingVehicle', 'adId']
        ) as Record<string, VehicleTypeForPriceMap>,
      providesTags: [{type: 'SourcingPriceMapVehicles'}],
    }),
    sourcingGetPriceMapStatistics: build.query<
      Record<string, PriceMapStatistics>,
      SimilarVehicleRequestBody
    >({
      query: (queryArg) => ({
        url: '/dms/v1/sourcing/similar-vehicle/statistics',
        method: 'GET',
        params: queryArg,
      }),
      transformResponse: (response: SimilarVehicleStatistics) => {
        const stats = response.groups;
        return reduceArrayToObject(
          stats.map((stat) => ({
            ...stat,
            key: renameStatisticsKeyToString(stat.key),
          })),
          ['key']
        ) as Record<string, PriceMapStatistics>;
      },
    }),
    sourcingDrillDown: build.query<DrillDownResponse, DrillDownApiArg>({
      query: (queryArg) => ({
        url: `/dms/v2/sourcing/drill-down`,
        method: 'POST',
        body: queryArg,
      }),
      // TODO T20-30972 - fix response on BE
      transformResponse: (response: DrillDownResponse, meta, arg: DrillDownApiArg) =>
        fixAlfaBugsInDrillDownResponse(
          arg.levels.map((level) => level.level),
          response
        ),
    }),
    sourcingGetSourcingServers: build.query<SourcingServer[], {countryCode: string}>({
      query: ({countryCode}) => ({
        url: '/common/v1/sourcing-servers',
        query: {
          countryCode,
        },
      }),
    }),
    sourcingPushVehiclesToSale: build.mutation<void, PushVehiclesToSaleApiArg>({
      query: ({externalIds}) => ({
        url: '/dms/v1/sourcing-vehicle/user-to-buy/buy-vehicles',
        method: 'POST',
        body: {sourcingVehiclesToBuy: externalIds},
      }),
      onQueryStarted: ({externalIds}, {dispatch, queryFulfilled}) => {
        queryFulfilled
          .then(() => {
            dispatch(
              sourcingRtkApi.endpoints.sourcingBulkDeleteUserToBuy.initiate({body: {externalIds}})
            );
          })
          .catch(() => {
            // We handle failure on component level – here we just skip initiating another mutation
          });
      },
    }),
    sourcingExportVehiclesToCsv: build.mutation<unknown, ExportVehiclesToCsvApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-to-buy/export/csv`,
        method: 'POST',
        body: {externalIds: queryArg.externalIds},
        responseHandler: 'text',
        cache: 'no-cache',
        params: {
          buyerCountry: queryArg.buyerCountry,
          currency: queryArg.currency,
          margin: queryArg.margin,
          orderBy: queryArg.orderBy,
        },
      }),
    }),
    sourcingGetComments: build.query<SourcingGetCommentsApiResponse, SourcingGetCommentsApiArg>({
      query: ({externalId}) => ({
        url: `/dms/v1/sourcing/comment/${externalId}`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SourcingComments', id: queryArg.externalId},
      ],
    }),
    sourcingCreateComment: build.mutation<
      SourcingCreateCommentApiResponse,
      SourcingCreateCommentApiArg
    >({
      query: ({externalId, body}) => ({
        url: `/dms/v1/sourcing/comment/${externalId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'SourcingComments', id: queryArg.externalId},
      ],
    }),
    sourcingDeleteComment: build.mutation<void, SourcingDeleteCommentApiArg>({
      query: ({externalId, uuid}) => ({
        url: `/dms/v1/sourcing/comment/${externalId}/${uuid}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'SourcingComments', id: queryArg.externalId},
      ],
    }),
    sourcingUpdateComment: build.mutation<
      SourcingUpdateCommentApiResponse,
      SourcingUpdateCommentApiArg
    >({
      query: ({externalId, uuid, body}) => ({
        url: `/dms/v1/sourcing/comment/${externalId}/${uuid}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'SourcingComments', id: queryArg.externalId},
      ],
    }),
    sourcingGetUserCostVehicleNote: build.query<
      DetailUserCostVehicleNoteApiResponse,
      DetailUserCostVehicleNoteApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/cost/vehicle/${queryArg.sourcingVehicleId}/note`,
      }),
    }),
    sourcingSetUserVehicleCostNote: build.mutation<void, SetUserVehicleCostNoteApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/cost/vehicle/${queryArg.sourcingVehicleId}/note`,
        method: 'PUT',
        body: queryArg.updateCostVehicleNoteRequestBody,
      }),
    }),
    sourcingGetDefaultUserPresetCost: build.query<DetailDefaultUserPresetCostApiResponse, void>({
      query: () => ({
        url: `/dms/v1/sourcing/cost/preset/_default`,
      }),
    }),
    sourcingCreateUserVehicleCost: build.mutation<void, CreateUserVehicleCostApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/cost/vehicle/${queryArg.sourcingVehicleId}/item`,
        method: 'POST',
        body: queryArg.userVehicleCostRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {
          type: 'SourcingVehicleCosts',
          id: queryArg.sourcingVehicleId,
        },
      ],
    }),
    sourcingCreateUserVehicleCosts: build.mutation<void, CreateUserVehicleCostsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/cost/vehicle/${queryArg.sourcingVehicleId}`,
        method: 'POST',
        body: queryArg.userVehicleCostsRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {
          type: 'SourcingVehicleCosts',
          id: queryArg.sourcingVehicleId,
        },
      ],
    }),
    sourcingGetUserCostVehicleList: build.query<
      UserCostVehicleListApiResponse,
      UserCostVehicleListApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/cost/vehicle/${queryArg.sourcingVehicleId}`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SourcingVehicleCosts', id: queryArg.sourcingVehicleId},
      ],
    }),
    sourcingDeleteUserVehicleCost: build.mutation<void, DeleteUserVehicleCostApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/cost/vehicle/${queryArg.sourcingVehicleId}/item/${queryArg.itemUuid}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {
          type: 'SourcingVehicleCosts',
          id: queryArg.sourcingVehicleId,
        },
      ],
    }),
    sourcingUpdateUserVehicleCost: build.mutation<void, UpdateUserVehicleCostApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/cost/vehicle/${queryArg.sourcingVehicleId}/item/${queryArg.itemUuid}`,
        method: 'PUT',
        body: queryArg.userVehicleCostRequestBody,
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {
          type: 'SourcingVehicleCosts',
          id: queryArg.sourcingVehicleId,
        },
      ],
    }),
    sourcingGetAdServerRedirect: build.query<AdServerRedirectResponse, AdServerRedirectApiArg>({
      query: (queryArg) => ({
        url: '/dms/v1/sourcing/change-url/sales-server-url',
        params: queryArg,
      }),
    }),
    sourcingSetUserSeen: build.mutation<CreateUserSeenApiResponse, CreateUserSeenApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-seen`,
        method: 'POST',
        body: queryArg.body,
      }),
      invalidatesTags: () => [{type: 'SourcingFilterPresetList'}],
      onQueryStarted: (queryArgs, mutationLifecycleApi) => {
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: [queryArgs.body.externalId]}},
          mutationLifecycleApi,
          'seen',
          true
        );
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: [queryArgs.body.externalId]}},
          mutationLifecycleApi,
          'isNew',
          false
        );
        optimisticUpdateVehicleDetailCache(
          {body: {externalIds: [queryArgs.body.externalId]}},
          mutationLifecycleApi,
          'seen',
          true
        );
      },
    }),
    sourcingGetPriceMap: build.query<GetPriceMapApiResponse, GetPriceMapApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/${queryArg.externalId}/price-map`,
        method: 'POST',
        body: queryArg.priceMapRequestBody,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SourcingPriceMap', id: queryArg.externalId},
      ],
    }),
    createSourcingVehicleOffer: build.query<
      CreateSourcingVehicleOfferApiResponse,
      CreateSourcingVehicleOfferApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/vehicle-offer`,
        method: 'POST',
        body: queryArg.createSourcingVehicleOfferRequestBody,
      }),
    }),
    getSourcingVehicleOffer: build.query<
      GetSourcingVehicleOfferApiResponse,
      GetSourcingVehicleOfferApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing/vehicle-offer/${queryArg.offerId}`,
      }),
    }),
    sourcingBulkDeleteUserCarvagoBuy: build.mutation<
      BulkDeleteUserCarvagoBuyApiResponse,
      BulkDeleteUserCarvagoBuyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-carvago-buy/_bulk`,
        method: 'DELETE',
        body: queryArg.bulkDeleteUserCarvagoBuyRequestBody,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(
          {body: {externalIds: args.bulkDeleteUserCarvagoBuyRequestBody.externalIds}},
          mutationLifecycleApi,
          'sourcingListUserCarvagoBuy'
        );
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: args.bulkDeleteUserCarvagoBuyRequestBody.externalIds}},
          mutationLifecycleApi,
          'carvagoBuy',
          false
        );
        optimisticUpdateVehicleDetailCache(
          {body: {externalIds: args.bulkDeleteUserCarvagoBuyRequestBody.externalIds}},
          mutationLifecycleApi,
          'carvagoBuy',
          false
        );
      },
      invalidatesTags: [{type: 'User'}, {type: 'SourcingCarvagoBuyList'}],
    }),
    sourcingBulkCreateUserCarvagoBuy: build.mutation<
      BulkCreateUserCarvagoBuyApiResponse,
      BulkCreateUserCarvagoBuyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-carvago-buy/_bulk`,
        method: 'POST',
        body: queryArg.bulkCreateUserCarvagoBuyRequestBody,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: args.bulkCreateUserCarvagoBuyRequestBody.externalIds}},
          mutationLifecycleApi,
          'carvagoBuy',
          true
        );
        optimisticUpdateVehicleDetailCache(
          {body: {externalIds: args.bulkCreateUserCarvagoBuyRequestBody.externalIds}},
          mutationLifecycleApi,
          'carvagoBuy',
          true
        );
      },
      invalidatesTags: [{type: 'User'}, {type: 'SourcingCarvagoBuyList'}],
    }),
    sourcingListUserCarvagoBuy: build.query<
      ListUserCarvagoBuyApiResponse,
      ListUserCarvagoBuyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-carvago-buy`,
        params: {
          size: queryArg.size,
          offset: queryArg.offset,
          currency: queryArg.currency,
          buyerCountry: queryArg.buyerCountry,
          margin: queryArg.margin,
          orderBy: queryArg.orderBy,
        },
      }),
      providesTags: () => [
        {type: 'SourcingAllListsWithSameResponseStructure'},
        {type: 'SourcingCarvagoBuyList'},
      ],
    }),
    sourcingCreateUserCarvagoBuy: build.mutation<
      CreateUserCarvagoBuyApiResponse,
      CreateUserCarvagoBuyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-carvago-buy`,
        method: 'POST',
        body: queryArg.createUserCarvagoBuyRequestBody,
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        const externalId = args.createUserCarvagoBuyRequestBody.externalId;
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: [externalId]}},
          mutationLifecycleApi,
          'carvagoBuy',
          true
        );
        optimisticUpdateVehicleDetailCache(
          {body: {externalIds: [externalId]}},
          mutationLifecycleApi,
          'carvagoBuy',
          true
        );
      },
      invalidatesTags: [{type: 'User'}, {type: 'SourcingCarvagoBuyList'}],
    }),
    sourcingDeleteUserCarvagoBuy: build.mutation<
      DeleteUserCarvagoBuyApiResponse,
      DeleteUserCarvagoBuyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sourcing-vehicle/user-carvago-buy/${queryArg.externalId}`,
        method: 'DELETE',
      }),
      onQueryStarted(args, mutationLifecycleApi) {
        optimisticRemoveVehiclesFromList(
          {body: {externalIds: [args.externalId]}},
          mutationLifecycleApi,
          'sourcingListUserCarvagoBuy'
        );
        optimisticUpdateVehicleListCaches(
          {body: {externalIds: [args.externalId]}},
          mutationLifecycleApi,
          'carvagoBuy',
          false
        );
        optimisticUpdateVehicleDetailCache(
          {body: {externalIds: [args.externalId]}},
          mutationLifecycleApi,
          'carvagoBuy',
          false
        );
      },
      invalidatesTags: [{type: 'User'}, {type: 'SourcingCarvagoBuyList'}],
    }),
  }),
  overrideExisting: false,
});

export {sourcingRtkApi as sourcingApi};

export const {
  useLazyExportAllUserToBuyToCsvQuery: useSourcingExportAllUserToBuyToCsvMutation,
  useSourcingCreatePresetMutation,
  useSourcingGetPresetsListQuery,
  useSourcingUpdatePresetMutation,
  useSourcingDeletePresetMutation,
  useSourcingGetCarFeaturesQuery,
  useLazySourcingGetCarFeaturesQuery,
  useLazySourcingGetPresetQuery,
  useSourcingReorderPresetsMutation,
  useSourcingGetTopSellingQuery,
  useSourcingGetPriceReportMutation,
  useSourcingVehicleDetailQuery,
  useSourcingVehicleListQuery,
  useSourcingGetVehicleCountQuery,
  useSourcingAllowedCountryAndCurrencyQuery,
  useSourcingListUserFavouriteQuery,
  useSourcingListUserToBuyQuery,
  useSourcingListUserHiddenQuery,
  useSourcingListUserComparisonQuery,
  useSourcingBulkCreateUserFavouriteMutation,
  useSourcingBulkDeleteUserFavouriteMutation,
  useSourcingReorderUserComparisonMutation,
  useSourcingBulkCreateUserComparisonMutation,
  useSourcingBulkDeleteUserComparisonMutation,
  useSourcingBulkCreateUserHiddenMutation,
  useSourcingBulkDeleteUserHiddenMutation,
  useSourcingBulkCreateUserToBuyMutation,
  useSourcingBulkDeleteUserToBuyMutation,
  useSourcingCreateUserVehicleSummaryMutation,
  useSourcingGetSalesClaimQuery,
  useSourcingSetSalesClaimMutation,
  useSourcingSetFeatureAsHighlightedMutation,
  useSourcingSetFeatureAsNotHighlightedMutation,
  useSourcingAddVehicleHighlightBulkMutation,
  useSourcingRemoveVehicleHighlightMutation,
  useSourcingRemoveVehicleHighlightBulkMutation,
  useSourcingAddVehicleShortcomingBulkMutation,
  useSourcingRemoveVehicleShortcomingMutation,
  useSourcingRemoveVehicleShortcomingBulkMutation,
  useSourcingGetPriceMapVehiclesQuery,
  useSourcingGetPriceMapStatisticsQuery,
  useSourcingDrillDownQuery,
  useSourcingGetSourcingServersQuery,
  useSourcingPushVehiclesToSaleMutation,
  useSourcingExportVehiclesToCsvMutation,
  useSourcingGetCommentsQuery,
  useSourcingCreateCommentMutation,
  useSourcingDeleteCommentMutation,
  useSourcingUpdateCommentMutation,
  useSourcingGetUserCostVehicleNoteQuery,
  useSourcingSetUserVehicleCostNoteMutation,
  useSourcingGetDefaultUserPresetCostQuery,
  useSourcingCreateUserVehicleCostMutation,
  useSourcingCreateUserVehicleCostsMutation,
  useSourcingGetUserCostVehicleListQuery,
  useSourcingDeleteUserVehicleCostMutation,
  useSourcingUpdateUserVehicleCostMutation,
  useLazySourcingGetAdServerRedirectQuery,
  useSourcingSetUserSeenMutation,
  useSourcingGetPriceMapQuery,
  useCreateSourcingVehicleOfferQuery,
  useLazyGetSourcingVehicleOfferQuery,
  useSourcingBulkDeleteUserCarvagoBuyMutation,
  useSourcingBulkCreateUserCarvagoBuyMutation,
  useSourcingListUserCarvagoBuyQuery,
  useSourcingCreateUserCarvagoBuyMutation,
  useSourcingDeleteUserCarvagoBuyMutation,
} = sourcingRtkApi;

export function updateClassifiedsVehicleSummaryInCaches(
  sourcingVehicleId: string,
  body: CreateUserVehicleSummaryRequestBody,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  sourcingRtkApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (!isSourcingListEndpointName(endpointName)) {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      mutationLifecycleApi.dispatch(
        sourcingRtkApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          setUserVehicleSummary(sourcingVehicleId, body, draft);
        })
      );
    });

  getSourcingVehicleDetailQueriesOriginalArgs(sourcingVehicleId, mutationLifecycleApi).forEach(
    (originalArgs) => {
      const patchResult = mutationLifecycleApi.dispatch(
        sourcingRtkApi.util.updateQueryData('sourcingVehicleDetail', originalArgs, (draft) => ({
          ...draft,
          vehicleSummary: {
            ...draft.vehicleSummary,
            purchasePrice: body.purchasePrice ?? null,
            sellingPrice: body.sellingPrice ?? null,
            comment: body.comment ?? null,
            ...body,
          },
        }))
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    }
  );
}

function getSourcingVehicleDetailQueriesOriginalArgs<T>(
  externalId: string,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>
) {
  return sourcingRtkApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingVehicleDetail'}])
    .filter(({endpointName, originalArgs}) => {
      if (endpointName !== 'sourcingVehicleDetail') {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }
      return externalId === originalArgs.externalId;
    })
    .map((item) => item.originalArgs);
}

function updateSourcingVehicleDetailCache(
  externalId: string,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>,
  updateFn: (draft: SourcingVehicleDetailApiResponse) => void
) {
  getSourcingVehicleDetailQueriesOriginalArgs(externalId, mutationLifecycleApi).forEach(
    (originalArgs) => {
      const patchResult = mutationLifecycleApi.dispatch(
        sourcingRtkApi.util.updateQueryData('sourcingVehicleDetail', originalArgs, updateFn)
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    }
  );
}

export function updateFeatureHighlightInCaches(
  externalId: string,
  featureKey: string,
  isHighlight: boolean,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  updateSourcingVehicleDetailCache(externalId, mutationLifecycleApi, (draft) => {
    assignSafely(draft.features, featureKey, {
      ...getSafely(draft.features, featureKey),
      isHighlight,
    });
  });
  updateAuctionDetailCacheByExternalId(externalId, mutationLifecycleApi, (draft) => {
    const current = draft.auctionVehicle.highlightFeatures ?? [];
    if (isHighlight) {
      if (!current.includes(featureKey)) {
        draft.auctionVehicle.highlightFeatures = [...current, featureKey];
      }
    } else {
      draft.auctionVehicle.highlightFeatures = current.filter((k) => k !== featureKey);
    }
  });
}

export function updateHighlightsInCaches(
  externalId: string,
  highlights: HighlightResponseBody[],
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  updateSourcingVehicleDetailCache(externalId, mutationLifecycleApi, (draft) => {
    draft.highlights = highlights;
  });
  updateAuctionDetailCacheByExternalId(externalId, mutationLifecycleApi, (draft) => {
    draft.highlights = highlights;
  });
}

export function removeHighlightFromCaches(
  externalId: string,
  highlightId: string,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  updateSourcingVehicleDetailCache(externalId, mutationLifecycleApi, (draft) => {
    draft.highlights = draft.highlights.filter((h) => h.id !== highlightId);
  });
  updateAuctionDetailCacheByExternalId(externalId, mutationLifecycleApi, (draft) => {
    draft.highlights = (draft.highlights ?? []).filter((h) => h.id !== highlightId);
  });
}

export function removeHighlightsBulkFromCaches(
  externalId: string,
  highlightIds: string[],
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  updateSourcingVehicleDetailCache(externalId, mutationLifecycleApi, (draft) => {
    draft.highlights = draft.highlights.filter((h) => !highlightIds.includes(h.id));
  });
  updateAuctionDetailCacheByExternalId(externalId, mutationLifecycleApi, (draft) => {
    draft.highlights = (draft.highlights ?? []).filter((h) => !highlightIds.includes(h.id));
  });
}

export function updateShortcomingsInCaches(
  externalId: string,
  shortcomings: ShortcomingResponseBody[],
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  updateSourcingVehicleDetailCache(externalId, mutationLifecycleApi, (draft) => {
    draft.shortcomings = shortcomings;
  });
  updateAuctionDetailCacheByExternalId(externalId, mutationLifecycleApi, (draft) => {
    draft.shortcomings = shortcomings;
  });
}

export function removeShortcomingFromCaches(
  externalId: string,
  shortcomingId: string,
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  updateSourcingVehicleDetailCache(externalId, mutationLifecycleApi, (draft) => {
    draft.shortcomings = draft.shortcomings.filter((s) => s.id !== shortcomingId);
  });
  updateAuctionDetailCacheByExternalId(externalId, mutationLifecycleApi, (draft) => {
    draft.shortcomings = (draft.shortcomings ?? []).filter((s) => s.id !== shortcomingId);
  });
}

export function removeShortcomingsBulkFromCaches(
  externalId: string,
  shortcomingIds: string[],
  mutationLifecycleApi: OmneticMutationLifecycleApi<unknown>
) {
  updateSourcingVehicleDetailCache(externalId, mutationLifecycleApi, (draft) => {
    draft.shortcomings = draft.shortcomings.filter((s) => !shortcomingIds.includes(s.id));
  });
  updateAuctionDetailCacheByExternalId(externalId, mutationLifecycleApi, (draft) => {
    draft.shortcomings = (draft.shortcomings ?? []).filter((s) => !shortcomingIds.includes(s.id));
  });
}

// Gets affected vehicles details (those queries which provide `SourcingVehicleDetail` tag) and set their list assignment
function optimisticUpdateVehicleDetailCache<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  list: List,
  isInList: boolean
) {
  sourcingRtkApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [{type: 'SourcingVehicleDetail'}])
    .forEach(({endpointName, originalArgs}) => {
      if (endpointName !== 'sourcingVehicleDetail') {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      if (queryArgs.body.externalIds.includes(originalArgs.externalId)) {
        const patchResult = mutationLifecycleApi.dispatch(
          sourcingRtkApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
            assignSafely(draft, list, isInList);
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
      }
    });
}

// Gets all cached queries sourcingVehicleList and set sourcing vehicles list assignment
function optimisticUpdateVehicleListCaches<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  list: List,
  isInList: boolean
) {
  sourcingRtkApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (!isSourcingListEndpointName(endpointName)) {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      const patchResult = mutationLifecycleApi.dispatch(
        sourcingRtkApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
          draft.data.forEach((vehicle) => {
            if (queryArgs.body.externalIds.includes(vehicle.sourcingVehicle.adId)) {
              assignSafely(vehicle, list, isInList);
            }
          });
        })
      );
      mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
    });
}

function optimisticRemoveVehiclesFromList<T extends {body: {externalIds: string[]}}>(
  queryArgs: T,
  mutationLifecycleApi: OmneticMutationLifecycleApi<T>,
  listToRemoveEndpointName?: string
) {
  sourcingRtkApi.util
    .selectInvalidatedBy(mutationLifecycleApi.getState(), [
      {type: 'SourcingAllListsWithSameResponseStructure'},
    ])
    .forEach(({endpointName, originalArgs}) => {
      if (!isSourcingListEndpointName(endpointName)) {
        throw new Error(`Invalid endpoint name (${endpointName}).`);
      }

      if (isNil(listToRemoveEndpointName) || endpointName === listToRemoveEndpointName) {
        const patchResult = mutationLifecycleApi.dispatch(
          sourcingRtkApi.util.updateQueryData(endpointName, originalArgs, (draft) => {
            draft.data = draft.data.filter(
              (vehicle) => !queryArgs.body.externalIds.includes(vehicle.sourcingVehicle.adId)
            );
          })
        );
        mutationLifecycleApi.queryFulfilled.catch(patchResult.undo);
      }
    });
}
